/**
  Intro:

  The base soompi component to use when rendering base route's virtual dom
  For common activity within a page, do use this component instead of
  `React.component`

  The purpose of the component is to allow for the following features:
   - register PV event on will mount (and duplicate check against duplicate pv)

  Usage:  ----------------------------------------------------------------------

    import SoompiComponent from 'scripts/components/base/SoompiComponent'

    class Home extends SoompiComponent {
      .
      .
      .
    }

  ------------------------------------------------------------------------------=
**/

import { Component } from 'react'
import VikiliticsWrapper from 'scripts/lib/Vikilitics/VikiliticsWrapper'
import ClientErrors from 'scripts/lib/ClientErrors'
import ConsentCookie from 'scripts/lib/ConsentCookie'
import {getDevice, generateSessionCookie, getBuildHash, getUserInfo} from 'scripts/lib/utils'
import locale, {getLang, langPrefix} from 'shared/i18n/react'

// KEY FUNCTIONS to hijack callbacks - placed outside the class to prevent
// overwriting behaviour in subclasses
const externalDep = {
  wrapDefaultCallback(cb, cbWrapper) {
    return () => {
      cbWrapper(...arguments)
      if (typeof cb === 'function') {
        // eslint-disable-next-line
        cb(...arguments)
      }
    }
  },

  componentWillMountCallback() {
    const { history } = this.props
    // Analytics toggle via cookie consent
    const consent = new ConsentCookie()
    if (history && history.listen && consent.hasConsent('analytics')) {
      this.unsubscribeLocationListener = history.listen(this.onLocationChange.bind(this))
      this.onLocationChange(history.location)
    }
  },

  componentWillUnmountCallback() {
    if (this.unsubscribeLocationListener) {
      this.unsubscribeLocationListener()
    }
  },
  // section requires a static data that can be altered by the new intance
  // making it available for the old instance to use
  // since the old instance fires on location change
  section: ''
}

export default class SoompiComponent extends Component {
  constructor(props) {
    super(props)

    // In this component we wrap componentWillMount and componentWillUnmount,
    // regardless of it being defined in the subclass, in order to provide
    // our own will mount and unmount behaviour along with subclass's versions
    // to listen and stop listening to certain page level events
    const wrapDefaultCallbackBinded = externalDep.wrapDefaultCallback.bind(this)
    if (this.componentWillMount) {
      this.componentWillMount = wrapDefaultCallbackBinded(
        this.componentWillMount.bind(this), externalDep.componentWillMountCallback.bind(this)
      )
    } else {
      this.componentWillMount = externalDep.componentWillMountCallback.bind(this)
    }

    if (this.componentWillUnmount) {
      this.componentWillUnmount = wrapDefaultCallbackBinded(
        this.componentWillUnmount.bind(this), externalDep.componentWillUnmountCallback.bind(this)
      )
    } else {
      this.componentWillUnmount = externalDep.componentWillUnmountCallback.bind(this)
    }
    this.locale = locale
    this.lang = getLang()
    this.langPrefix = langPrefix()
    this.allLangs = locale.getAvailableLanguages()
    /* global __isBrowser__ */
    this.isDataAvailable = __isBrowser__ && window.__serverData__
  }

  // this is the current instance, whenever section is changed we store it
  // on to the static variable
  shouldComponentUpdate(nextProps) {
    if (nextProps.section) {
      externalDep.section = nextProps.section
    }
    return true
  }

  onLocationChange(location) {
    // Timeout to push the function down the queue, after props has been updated
    // this will in turn fire once the location has changed, but the data was already
    // set previously from the old instance
    setTimeout(() => {
      const currentPathname = this.props.location && this.props.location.pathname
      if (currentPathname !== this.lastLocationPathname) {
        this.lastLocationPathname = currentPathname
        const { section, articleTag } = this.props
        VikiliticsWrapper.sendPvEvent(this.getPageId(), {
          article_id: this.getArticleId(), // article page tracking
          category_id: this.getCategoryId(), // category page tracking
          tag_id: this.getTagId(), // tag page tacking
          fanclub_id: this.getFanClubId(), // fanclub page tacking
          user_id: getUserInfo(this.lang),
          prev_section_id: section, // this.getExternalDepSection(),
          device: getDevice(),
          as_id: generateSessionCookie(),
          app_ver: getBuildHash(),
          site: this.lang,
          article_tag: articleTag // this.getExternalDepArticleTag()
        })
      }
    })
  }

  // this gets fired whenever a location has changed
  // we must change the seciton value here to empty
  // because we are now in the new page
  getExternalDepSection() {
    const temp = externalDep.section
    externalDep.section = ''
    return temp
  }

  getPathMatches() {
    let matches
    try {
      matches = this.props.match.params
    } catch (e) {
      ClientErrors({
        message: 'Base Component match is not specified',
        error: e
      })
      matches = {}
    }
    return matches
  }

  getPageId() {
    return this.props.route.name
  }

  getArticleId() {
    let articleId
    const pattern = new RegExp(`^[/(${this.allLangs.join('|')})]*/article`)
    if (this.props && this.props.location && pattern.test(this.props.location.pathname)) {
      let matches = this.getPathMatches()
      articleId = matches.id
    }
    return articleId
  }

  getCategoryId() {
    let categoryId
    const pattern = new RegExp(`^[/(${this.allLangs.join('|')})]*/category`)
    if (this.props && this.props.location && pattern.test(this.props.location.pathname)) {
      let matches = this.getPathMatches()
      categoryId = matches.slug
    }
    return categoryId
  }

  getTagId() {
    let tagId
    const pattern = new RegExp(`^[/(${this.allLangs.join('|')})]*/tag`)
    if (this.props && this.props.location && pattern.test(this.props.location.pathname)) {
      let matches = this.getPathMatches()
      tagId = matches.slug
    }
    return tagId
  }

  getFanClubId() {
    let fanclubId
    const pattern = new RegExp(`^[/(${this.allLangs.join('|')})]*/fanclub`)
    if (this.props && this.props.location && pattern.test(this.props.location.pathname)) {
      let matches = this.getPathMatches()
      fanclubId = matches.slug
    }
    return fanclubId
  }
}



// WEBPACK FOOTER //
// ./assets/js/components/base/SoompiComponent.jsx